import axios from 'axios';
import { getManagerSession } from './managerSession';

// Desktop app: the React build is served by the local engine itself, so the
// API is same-origin ("" -> "/api/"); the shell may also pin an explicit base.
const BASE_URL = (typeof window !== 'undefined' && window.kipara && window.kipara.apiBase != null)
  ? window.kipara.apiBase
  : (process.env.REACT_APP_BASE_URL || '');
const API_URL = `${BASE_URL}/api/`;

// Create axios instance with base URL
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests if it exists in localStorage
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Authentication service
const authService = {
  // Login user with email and password
  login: async (email, password) => {
    try {
      const response = await apiClient.post('auth/login/', { email, password });
      if (response.data.token) {
        localStorage.setItem('user', JSON.stringify(response.data));
        localStorage.setItem('token', response.data.token);
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error.response?.data || { message: 'Unable to connect to server' };
    }
  },
  
  // Logout user
  logout: async () => {
    try {
      await apiClient.post('auth/logout/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
  },

  // Get current user data
  getCurrentUser: () => {
    return JSON.parse(localStorage.getItem('user'));
  },
  
  // Check if user is logged in
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },
  
  // Trois types de COMPTE : 'admin' = propriétaire (tout), 'manager' (tout
  // sauf gérer les comptes et les personnes), 'seller' (accès limité).
  // isOwner = propriétaire seulement ; isAdmin = accès complet (propriétaire
  // OU manager) — c'est lui que les pages « réservées » consultent.
  isOwner: () => {
    const user = authService.getCurrentUser();
    return !!user && user.role === 'admin';
  },

  isAdmin: () => {
    const user = authService.getCurrentUser();
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'manager') return true;
    // Caisse en mode manager (un manager reconnu) : sans limite pour un moment.
    return user.role === 'seller' && !!getManagerSession();
  },

  // Compte caisse (partagé en boutique) — l'ancien rôle « vendeur ».
  isCaisse: () => {
    const user = authService.getCurrentUser();
    return !!user && user.role === 'seller';
  },

  // Historical name, same rule as isAdmin.
  isManagerOrAdmin: () => authService.isAdmin(),
  
  // Check if user is a seller
  isSeller: () => {
    const user = authService.getCurrentUser();
    return user && user.role === 'seller';
  },
  
  // Check if user has any staff role (seller or staff)
  isStaff: () => {
    const user = authService.getCurrentUser();
    return user && (user.role === 'staff' || user.role === 'seller');
  },
  
  // Check if user has any of the specified roles
  hasRole: (roles) => {
    const user = authService.getCurrentUser();
    if (!user || !user.role) return false;
    
    // Check if user's role is in the list of allowed roles. A manager account
    // passes wherever the owner ('admin') does — owner-only screens (accounts,
    // people) ask isOwner() instead.
    if (user.role === 'manager' && roles.includes('admin')) return true;
    if (user.role === 'seller' && roles.includes('admin') && getManagerSession()) return true;
    return roles.includes(user.role);
  },
  
  // Check if user has access to facturation and suppliers pages
  hasFacturationAccess: () => {
    const user = authService.getCurrentUser();
    if (!user) return false;
    
    // Owner and manager always have access (a caisse in manager mode too)
    if (user.role === 'admin' || user.role === 'manager') return true;
    if (user.role === 'seller' && getManagerSession()) return true;
    
    // Sellers need the specific permission
    return user.has_facturation_access === true;
  },
  
  // Get all users (admin only)
  getAllUsers: async () => {
    try {
      console.log('Fetching all users...');
      const response = await apiClient.get('auth/users/');
      console.log('API Response:', response);
      
      // Check if the response has a results key (in case of pagination)
      if (response.data && response.data.results) {
        console.log('Response contains paginated data, returning results', response.data.results);
        return response.data.results;
      }
      
      console.log('Returning data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response) {
        console.error('API Error Status:', error.response.status);
        console.error('API Error Data:', error.response.data);
      }
      throw error.response?.data || { message: 'Unable to fetch users' };
    }
  },
  
  // Get a specific user (admin only)
  getUser: async (userId) => {
    try {
      const response = await apiClient.get(`auth/users/${userId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error.response?.data || { message: 'Unable to fetch user' };
    }
  },
  
  // Create a new user (admin only)
  createUser: async (userData) => {
    try {
      console.log('Creating user with data:', userData);
      
      // Ensure required fields are present
      const requiredFields = ['email', 'username', 'password', 'confirm_password', 'role'];
      requiredFields.forEach(field => {
        if (!userData[field]) {
          throw new Error(`Missing required field: ${field}`);
        }
      });
      
      // Set is_active to true by default if not provided
      const dataToSubmit = {
        ...userData,
        is_active: userData.is_active !== undefined ? userData.is_active : true
      };
      
      const response = await apiClient.post('auth/users/', dataToSubmit);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error status:', error.response.status);
      } else {
        console.error('Error message:', error.message);
      }
      
      throw error.response?.data || { message: error.message || 'Unable to create user' };
    }
  },
  
  // Update a user (admin only)
  updateUser: async (userId, userData) => {
    try {
      const response = await apiClient.put(`auth/users/${userId}/`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error.response?.data || { message: 'Unable to update user' };
    }
  },
  
  // Partially update a user (e.g. toggle is_active / has_facturation_access) — admin only.
  updateUserStatus: async (userId, patch) => {
    try {
      const response = await apiClient.patch(`auth/users/${userId}/`, patch);
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error.response?.data || { message: 'Unable to update user' };
    }
  },

  // Delete a user (admin only)
  deleteUser: async (userId) => {
    try {
      await apiClient.delete(`auth/users/${userId}/`);
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error.response?.data || { message: 'Unable to delete user' };
    }
  }
};

export default authService;
export { apiClient };
